import { useState, useCallback } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { loadImageAsCanvas, flipImageCanvas, rotateImageCanvas90, canvasToBlobMatchingFormat } from "./operations";
import { RotateCw, RotateCcw } from "lucide-react";

const TOOL_INFO_CONTENT = {
  what: `Flips an image horizontally or vertically, or rotates it 90 degrees at a time (clockwise, counter-clockwise, or a full 180) — the same fix used for PDFs, but for a standalone image file. Common reasons this comes up: a photo that saved sideways or upside down, a scanned image that needs mirroring, or an image that needs to match the orientation of others in a set.`,
  how: `Upload your image, choose flip or one of the rotate options, and download the corrected version. Rotations are in fixed 90-degree steps — there's no free-angle rotation slider here, since a precise small-angle correction (straightening a slightly crooked scan, for instance) is a different job than fixing a sideways or upside-down image.`,
  tip: `A photo taken with a phone held sideways often looks correct on the phone itself but sideways everywhere else — this is the quick fix for that specific, very common problem, since the phone's own orientation metadata doesn't always get read correctly by every program or platform the photo ends up on.`,
};

export default function FlipRotateImage() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setError("");
    if (f.type && !f.type.startsWith("image/")) { setError("That is not an image file. Choose a JPG, PNG, WebP or other image."); return; }
    setFile(f);
    loadImageAsCanvas(f).then(c => {
      setCanvas(c);
      setPreview(c.toDataURL());
    }).catch((e) => { setError(e?.message || "This file could not be opened as an image. It may be damaged or not really an image."); setFile(null); setCanvas(null); setPreview(""); });
  }, []);
  const [rotated, setRotated] = useState(false);
  const [flipped, setFlipped] = useState(false);

  const transform = (type: "flipH" | "flipV" | "rotateCW" | "rotateCCW" | "rotate180") => {
    if (!canvas) return;
    if (type === "rotateCW") rotateImageCanvas90(canvas);
    // CCW and 180 are both just the same real 90-degree rotation applied
    // multiple times (3x for a full turn back = counter-clockwise, 2x for
    // a half turn) rather than separate rotation math -- simpler and
    // exactly equivalent for a single-image operation like this, where
    // the extra calls cost nothing noticeable.
    else if (type === "rotateCCW") { rotateImageCanvas90(canvas); rotateImageCanvas90(canvas); rotateImageCanvas90(canvas); }
    else if (type === "rotate180") { rotateImageCanvas90(canvas); rotateImageCanvas90(canvas); }
    else flipImageCanvas(canvas, type === "flipH" ? "horizontal" : "vertical");
    if (type === "flipH" || type === "flipV") setFlipped(true); else setRotated(true);
    setPreview(canvas.toDataURL());
  };

  const download = async () => {
    if (!canvas || !file) return;
    // Same fix as ImageFilters: use the robust shared format detection
    // instead of a naive file.type-or-PNG fallback, which is what caused
    // a 26KB photo to balloon to 384KB after a simple flip or rotate.
    const { blob, filename } = await canvasToBlobMatchingFormat(canvas, file);
    downloadBlob(blob, `${rotated ? "rotated_" : flipped ? "flipped_" : ""}${filename}`);
  };

  return (
    <ToolContainer>
      {!file ? (
        <>
        <FileDropZone gateAtDownload accept="image/*" onFiles={handleFiles} label="Drop an image" description="Flip or rotate images" />
          {error && <p role="alert" className="text-xs text-destructive mt-2">{error}</p>}
        </>
      ) : (
        <div className="space-y-4">
          {preview && <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded-lg border" />}
          <div className="flex gap-2 justify-center flex-wrap">
            <Button variant="outline" size="sm" onClick={() => transform("flipH")}>Flip Horizontal</Button>
            <Button variant="outline" size="sm" onClick={() => transform("flipV")}>Flip Vertical</Button>
            <Button variant="outline" size="sm" onClick={() => transform("rotateCCW")} className="gap-1.5">
              <RotateCcw className="h-3.5 w-3.5" /> Rotate 90° CCW
            </Button>
            <Button variant="outline" size="sm" onClick={() => transform("rotateCW")} className="gap-1.5">
              <RotateCw className="h-3.5 w-3.5" /> Rotate 90° CW
            </Button>
            <Button variant="outline" size="sm" onClick={() => transform("rotate180")}>Rotate 180°</Button>
          </div>
          <p className="text-xs text-muted-foreground text-center">Metadata such as EXIF (camera details, date, GPS location) is removed from the downloaded image.</p>
          <div className="flex gap-3 justify-center">
            <DownloadButton onClick={download} label="Download" />
            <Button variant="outline" onClick={() => { setFile(null); setPreview(""); setRotated(false); setFlipped(false); }}>Reset</Button>
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
